import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Colors,
  Field,
  FieldError,
  FieldLabel,
  Spacing,
  TextInput,
  Textarea,
  createClasses,
} from '@kp/react-ui';

const classes = createClasses({
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  fields: {
    display: 'flex',
    flexDirection: 'column',
    gap: Spacing.md,
    padding: `${Spacing.md} ${Spacing.lg}`,
  },
  row: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    gap: Spacing.md,
    '@media (max-width: 480px)': {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: Spacing.xs,
    padding: `${Spacing.sm} ${Spacing.lg}`,
    borderTop: `1px solid ${Colors.Neutral.borderStrong}`,
  },
});

export type EmailFormData = {
  fullName: string;
  email: string;
  subject: string;
  message: string;
};

export interface EmailFormProps {
  onSubmit: (data: EmailFormData) => void;
  onCancel: () => void;
  isLoading: boolean;
}

export const EmailForm = ({
  onSubmit,
  onCancel,
  isLoading,
}: EmailFormProps) => {
  const { t } = useTranslation();
  const {
    getValues,
    handleSubmit,
    register,
    formState: { errors },
  } = useFormContext<EmailFormData>();

  return (
    <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
      <div className={classes.fields}>
        <div className={classes.row}>
          {/* Sender fields come from the auth user and are not editable;
              left unregistered so RHF keeps their defaultValues on submit */}
          <Field>
            <FieldLabel>{t('emailForm.fields.fullName')}</FieldLabel>
            <TextInput
              fullWidth
              disabled
              readOnly
              value={getValues('fullName')}
            />
          </Field>
          <Field>
            <FieldLabel>{t('emailForm.fields.email')}</FieldLabel>
            <TextInput fullWidth disabled readOnly value={getValues('email')} />
          </Field>
        </div>
        <Field>
          <FieldLabel>{t('emailForm.fields.subject')}</FieldLabel>
          <TextInput
            fullWidth
            error={Boolean(errors.subject)}
            {...register('subject', {
              required: t('emailForm.errors.required'),
            })}
          />
          {errors.subject && <FieldError>{errors.subject.message}</FieldError>}
        </Field>
        <Field>
          <FieldLabel>{t('emailForm.fields.message')}</FieldLabel>
          <Textarea
            rows={5}
            fullWidth
            error={Boolean(errors.message)}
            {...register('message', {
              required: t('emailForm.errors.required'),
            })}
          />
          {errors.message && <FieldError>{errors.message.message}</FieldError>}
        </Field>
      </div>
      <div className={classes.footer}>
        <Button type="button" variant="secondary" onClick={onCancel}>
          {t('emailForm.buttons.cancel')}
        </Button>
        <Button type="submit" variant="primary" loading={isLoading}>
          {t('emailForm.buttons.send')}
        </Button>
      </div>
    </form>
  );
};
